import {
  Autocomplete,
  Box,
  TextField,
  Typography,
  type AutocompleteRenderInputParams,
} from "@mui/material";
import {
  useCallback,
  useMemo,
  type HTMLAttributes,
  type Key,
  type SyntheticEvent,
} from "react";
import { type OrderStatus } from "../../../generated/graphql";
import {
  MAX_PICKED_ORDERS,
  mergeSelectedOrderOptions,
  orderOptionDetails,
  orderOptionLabel,
  type BenchOrderOption,
} from "../bench-run";
import useBenchOrderSearch from "../hooks/use-bench-order-search";

const EMPTY_STATUSES: OrderStatus[] = [];

const isSameOrder = (option: BenchOrderOption, value: BenchOrderOption) =>
  option.uuid === value.uuid;

const getOrderName = (option: BenchOrderOption) => option.name;

/** The backend already filtered; MUI's own text filter would drop results. */
const keepServerResults = (options: BenchOrderOption[]) => options;

const renderOrderOption = (
  props: HTMLAttributes<HTMLLIElement> & { key?: Key },
  option: BenchOrderOption,
) => {
  const { key: _key, ...optionProps } = props;
  return (
    <li key={option.uuid} {...optionProps}>
      <Box>
        <Typography variant="body2">{option.name}</Typography>
        <Typography color="text.secondary" variant="caption">
          {orderOptionDetails(option)}
        </Typography>
      </Box>
    </li>
  );
};

/** The texts and input every order picker shares. */
const useOrderPickerChrome = ({
  partnershipUuid,
  label,
  helperText,
  errorMessage,
}: {
  partnershipUuid: string | null;
  label: string;
  helperText: string | undefined;
  errorMessage: string | null;
}) => {
  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        error={errorMessage !== null}
        helperText={
          errorMessage === null
            ? helperText
            : `Could not search orders: ${errorMessage}`
        }
        label={label}
        placeholder="Type an order name, HAWB or MAWB"
      />
    ),
    [errorMessage, helperText, label],
  );
  const noOptionsText =
    partnershipUuid === null
      ? "Pick a customer and partnership first."
      : "No orders match.";
  return { renderInput, noOptionsText };
};

type CommonProps = {
  readonly partnershipUuid: string | null;
  readonly orderStatuses?: readonly OrderStatus[];
  readonly label: string;
  readonly helperText?: string;
};

type BenchOrdersPickerProps = CommonProps & {
  readonly value: BenchOrderOption[];
  readonly onChange: (value: BenchOrderOption[]) => void;
};

/** Picks several of the partnership's orders, searching as the operator types. */
export const BenchOrdersPicker = ({
  partnershipUuid,
  orderStatuses = EMPTY_STATUSES,
  label,
  helperText,
  value,
  onChange,
}: BenchOrdersPickerProps) => {
  const { results, loading, errorMessage, handleInputChange } =
    useBenchOrderSearch({ partnershipUuid, orderStatuses });
  const { renderInput, noOptionsText } = useOrderPickerChrome({
    partnershipUuid,
    label,
    helperText,
    errorMessage,
  });
  const options = useMemo(
    () => mergeSelectedOrderOptions(value, results),
    [results, value],
  );
  const handleChange = useCallback(
    (_: SyntheticEvent, next: BenchOrderOption[]) => {
      onChange(next);
    },
    [onChange],
  );
  const atCap = value.length >= MAX_PICKED_ORDERS;
  const isOptionDisabled = useCallback(() => atCap, [atCap]);

  return (
    <Autocomplete
      disableCloseOnSelect
      filterSelectedOptions
      multiple
      disabled={partnershipUuid === null}
      filterOptions={keepServerResults}
      getOptionDisabled={isOptionDisabled}
      getOptionLabel={getOrderName}
      isOptionEqualToValue={isSameOrder}
      loading={loading}
      loadingText="Searching orders…"
      noOptionsText={noOptionsText}
      options={options}
      renderInput={renderInput}
      renderOption={renderOrderOption}
      value={value}
      onChange={handleChange}
      onInputChange={handleInputChange}
    />
  );
};

type BenchOrderPickerProps = CommonProps & {
  readonly value: BenchOrderOption | null;
  readonly onChange: (value: BenchOrderOption | null) => void;
};

/** Picks one of the partnership's orders, searching as the operator types. */
export const BenchOrderPicker = ({
  partnershipUuid,
  orderStatuses = EMPTY_STATUSES,
  label,
  helperText,
  value,
  onChange,
}: BenchOrderPickerProps) => {
  const { results, loading, errorMessage, handleInputChange } =
    useBenchOrderSearch({ partnershipUuid, orderStatuses });
  const { renderInput, noOptionsText } = useOrderPickerChrome({
    partnershipUuid,
    label,
    helperText,
    errorMessage,
  });
  const options = useMemo(
    () => mergeSelectedOrderOptions(value === null ? [] : [value], results),
    [results, value],
  );
  const handleChange = useCallback(
    (_: SyntheticEvent, next: BenchOrderOption | null) => {
      onChange(next);
    },
    [onChange],
  );

  return (
    <Autocomplete
      disabled={partnershipUuid === null}
      filterOptions={keepServerResults}
      getOptionLabel={orderOptionLabel}
      isOptionEqualToValue={isSameOrder}
      loading={loading}
      loadingText="Searching orders…"
      noOptionsText={noOptionsText}
      options={options}
      renderInput={renderInput}
      renderOption={renderOrderOption}
      value={value}
      onChange={handleChange}
      onInputChange={handleInputChange}
    />
  );
};
